import type { PageHelp } from "../types";

export const insightsPageHelp: Record<string, PageHelp> = {
  "command-center": {
    title: "Command Center",
    summary:
      "The big-picture page. It shows your monthly recurring revenue (MRR) against your goal, the milestones you must clear to scale (revenue gates), the outside waits holding you back (clocks), and every subscription.",
    steps: [
      { title: "Read the ring", body: "The large number is your current MRR. The ring fills to the % to goal shown underneath." },
      { title: "Check accounts to go", body: "Under the ring, active, trial and accounts to go tell you how many more paying accounts close the gap." },
      { title: "Study the curve", body: "The chart titled MRR to your goal draws one point per day, so you can see whether growth is speeding up or flat." },
      { title: "Review the Revenue gates", body: "Each row is a milestone. A red dot means blocked, orange means in progress, grey means open." },
      { title: "Watch the clocks", body: "The clock cards on the right are waits you cannot speed up. The ETA badge is your own estimate." },
      { title: "Scan Active subscriptions", body: "The list at the bottom shows each subscription with a trial or active badge and its monthly amount." },
    ],
    tips: [
      "If the ring is empty, set your MRR goal in Settings, Content. The page cannot measure progress without a goal.",
      "Trials never count toward MRR. A trial only lifts the number once it becomes an active subscription.",
      "Gates and clocks are text you type in Settings, Content. Nothing updates them automatically.",
    ],
    articles: ["insights-command-center-reference", "insights-mrr-to-goal", "insights-revenue-gates-clocks"],
  },

  "support-insights": {
    title: "Support Insights",
    summary: "How your ticket desk is doing, what customers keep asking, and which customers might leave or buy more. Every number opens the tickets behind it.",
    steps: [
      { title: "Pick the window", body: "7, 30 or 90 days at the top right. The small line under Opened and Done compares with the period before." },
      { title: "Read the six tiles", body: "Opened, Done, Open now, Overdue, typical first reply and typical time to finish (in working hours, with % on time). Click any tile to see its tickets." },
      { title: "Look at the chart and tables", body: "Blue bars are opened, green are done. The tables split it by request type, by person and by how requests arrive." },
      { title: "Check Customers to watch", body: "Could leave and May want more badges come from plain rules. Click a reason to see the tickets behind it." },
      { title: "Fix what keeps coming up", body: "Under Asked again and again, Start a wiki page makes an unpublished draft with the examples filled in." },
    ],
    tips: [
      "Monthly revenue next to a customer only shows for people with the MRR permission.",
      "Nothing here is AI-guessed and nothing is sent to customers.",
    ],
    articles: ["insights-support-insights", "tickets-overview", "tickets-hours-vip-cap"],
  },

  "sales-tracking": {
    title: "Sales Tracking",
    summary:
      "Your real close rate against the 50% the plan assumes, plus cash collected and calls per day. Close and cash are automatic from GoHighLevel now, so this page answers one question with no manual entry required: is the sales model actually working?",
    steps: [
      { title: "Read the three cards", body: "Close rate, Cash collected and Live calls / day cover the dates picked in the date filter at the top right (last 30 days by default). Click any card to see the accounts behind the number." },
      { title: "Toggle First touch only", body: "Switch it on to count only each account's very first booking — a re-booked follow-up showing or closing again no longer counts toward these rates." },
      { title: "Compare close rate with the plan", body: "The Close rate vs the 50% plan bar turns green at 50% or higher and orange below it." },
      { title: "Follow the money", body: "Cash collected by day shows the deal value on the day an account closed, day by day." },
      { title: "Open a recent call", body: "Click any row under Recent calls to open that account." },
    ],
    tips: [
      "Close rate and cash update themselves the moment a deal's status or value changes in GoHighLevel — no logging required.",
      "Call Logger is now just for follow-up dates and notes; it no longer drives these numbers.",
      "Cash and money figures are hidden from roles without deal-money permission.",
    ],
    articles: ["insights-sales-tracking-reference", "insights-close-rate", "insights-date-ranges-data-modes"],
  },

  "setter-tracking": {
    title: "Setter Tracking",
    summary:
      "How well booked calls turn into shows, and how leads move through the setter funnel. The key numbers are Show rate, C-SUR versus NC-SUR, and Live calls / day.",
    steps: [
      { title: "Read the four cards", body: "Show rate, C-SUR, NC-SUR and Live calls / day summarize the dates picked in the date filter at the top right (last 30 days by default)." },
      { title: "Toggle First touch only", body: "Switch it on to count only each account's very first booking — a re-booked follow-up showing up again no longer counts toward these rates. It's off by default." },
      { title: "Compare confirmed with non-confirmed", body: "In Confirmed vs non-confirmed show-up, a big gap proves that confirming calls pays off." },
      { title: "Compare Setter with Direct", body: "Setter vs direct-booking show rate shows whether human-booked calls show up better than self-booked ones." },
      { title: "Walk the Setter funnel", body: "Leads, Convos, Sets, Shows and Closes show where prospects drop out. The badges give Lead to Set, sets per day and speed-to-lead." },
      { title: "Play in Numbers math", body: "Drag the sliders to model what a change in cost per lead or show rate would do. Nothing is saved." },
      { title: "Read Dials, sets & speed-to-lead", body: "Bars are dials and sets per day; the line is average speed-to-lead in seconds (lower is better)." },
    ],
    tips: [
      "To lift C-SUR and show rate, confirm every upcoming call on the Booking Board.",
      "Convos can be more than Leads because a reply on each day counts once per day.",
      "The funnel mixes date bases: leads by created date, sets by booking date, shows and closes by call date.",
    ],
    articles: ["insights-setter-tracking-reference", "insights-show-rate-sur", "insights-numbers-math-model"],
  },

  "team-dashboard": {
    title: "Team Dashboard",
    summary: "Schedule Calls, Live Calls, Show %, Offers and money, broken down by the person who set or closed the call, and rolled up by year, quarter and month.",
    steps: [
      { title: "Pick a person", body: "Team shows everyone combined. Click a name to filter to just their numbers." },
      { title: "Expand a period", body: "Click a year to see its quarters, and a quarter to see its months." },
      { title: "Choose your columns", body: "Click Columns to show or hide any metric. Your choice is remembered on this device." },
      { title: "Export", body: "Export CSV downloads the periods currently expanded, with the columns you see." },
    ],
    tips: [
      "Schedule Calls and Live Calls are attributed to who booked the call. This only works for bookings made in Flowio, and for GHL-synced bookings where the lead's contact history has been pulled — a booking with nobody attributed doesn't count toward anyone's numbers yet.",
      "Offers is every call a closer logged. Flowio doesn't yet track a pitched-but-no-offer call separately from one that was.",
      "Offer 1 and Offer 2 are your trial and full-price offers, labelled from the prices set in Settings.",
    ],
    articles: ["insights-setter-tracking-reference"],
  },

  "account-matrix": {
    title: "Account Matrix",
    summary: "Every account in one table, with the account name pinned on the left. See at a glance which GHL pipeline it is in, who set it, whether it showed, and what it was worth.",
    steps: [
      { title: "Pick the dates", body: "Use the date filter at the top right. Choose whether it filters by when the lead was created or by the date of its booked call." },
      { title: "Read the stage chips", body: "Lead, Convo, Set, Showed and Closed show how many accounts got that far. Click a chip to filter the table to it." },
      { title: "Filter and search", body: "Narrow by pipeline, status, lead source, or setter versus direct, or search by account, owner or city." },
      { title: "Choose your columns", body: "Click Columns to show or hide any of them. Your choice is remembered on this device." },
      { title: "Sort and open", body: "Click a column heading to sort. Click a row to open the account. The bottom row totals what is shown." },
      { title: "Export", body: "Export CSV downloads exactly the rows and columns you see." },
    ],
    tips: [
      "Pipeline and Pipeline stage come straight from GoHighLevel. A blank means the account has not been synced from a pipeline.",
      "Bookings above 1 usually means a reschedule or a duplicate booking. Open the account to check.",
      "Cash collected is hidden if you don't have deal-money permission.",
    ],
    articles: ["insights-setter-tracking-reference", "insights-booking-board-guide"],
  },

  "booking-board": {
    title: "Booking Board",
    summary:
      "Every booked call across the agency in one place. Confirm upcoming calls, mark who showed up, and correct mistakes. Your show rate is built from what you mark here.",
    steps: [
      { title: "Set the filters", body: "Use the search box, the date window (Last 14 days, Last 30 days, Last 90 days, All time), the source filter and the outcome filter. Clear resets them." },
      { title: "Toggle First touch only", body: "Narrows the Show rate badge to each account's very first booking — re-booked follow-ups still appear in every list below, just not in that badge." },
      { title: "Work Needs confirming", body: "Click the message icon to copy a confirmation text, send it, then click Confirm when the lead replies." },
      { title: "Review Confirmed · upcoming", body: "After each call, click Showed if they attended, or the X icon if they did not." },
      { title: "Check Resolved", body: "Decided calls sit here with a Showed or No-show badge. Use the check or X to correct a mistake." },
      { title: "Undo if needed", body: "The undo arrow clears the outcome and sends the booking back to upcoming." },
      { title: "Open the account", body: "Click a booking row to open the account behind it." },
    ],
    tips: [
      "The Show rate badge at the top counts every decided booking in your filters. The outcome filter does not change it.",
      "Marking a no-show notifies the team, so correct a wrong click with the undo arrow rather than leaving it.",
      "The board always includes the next 30 days, so upcoming calls never drop off when you shorten the window.",
    ],
    articles: ["insights-booking-board-guide", "insights-show-rate-sur"],
  },

  "status-report": {
    title: "Daily Status Report",
    summary:
      "One post a day in the same shape every day. The five-stage scoreboard fills itself from your tracking data, and you add the summary, what you finished and what is next.",
    steps: [
      { title: "Check the header", body: "It shows the Day number, days to your goal, MRR and Clients. Add a Header badge if you want a label such as ADS LIVE." },
      { title: "Read the Scoreboard", body: "Leads in, Contacted, Calls booked, Calls shown and Closed fill in for today, with a wk figure for the last 7 days." },
      { title: "Write the Summary", body: "Two or three sentences on what today was about and what tomorrow depends on." },
      { title: "List Completed today", body: "Type each finished item and press Enter or click the plus icon." },
      { title: "Tend Upcoming", body: "Queue future items, and tick them when done. They carry over to tomorrow." },
      { title: "Copy for Slack", body: "Click Copy for Slack and paste the text into your channel." },
      { title: "Save today", body: "Click Save today to file the report. The summary and completed list clear; upcoming items stay." },
    ],
    tips: [
      "Copy for Slack does not save. Click Save today as well so the report appears under Filed reports.",
      "Items that are two or more days old show their age, which is a nudge to finish or delete them.",
      "Set your Day one and goal deadline in Settings, Content so the Day number and countdown are right.",
    ],
    articles: ["insights-status-report-guide", "insights-date-ranges-data-modes"],
  },

  audits: {
    title: "Audit Reports",
    summary:
      "Pull a GHL business audit for a prospect and read it in Flowio's own layout: scores, website speed, directory listings, reviews and where the business ranks locally.",
    steps: [
      { title: "Paste the link", body: "Paste the GHL share link (or just the report id) into the box at the top." },
      { title: "Choose Add as a new prospect if needed", body: "Tick it only when you want Flowio to create an account when nothing matches." },
      { title: "Click Pull report", body: "The report is saved and opens. It links to an existing account when the website or phone matches." },
      { title: "Or click Sync from GHL", body: "This fetches every audit already in your GHL prospecting tool, with no pasting." },
      { title: "Read the report", body: "Work down Scores, Website performance, Listings, Online reputation, Local rank, Marketing tech stack and Google Business Profile." },
      { title: "Use the list", body: "Back on All reports, click any row to reopen it. Delete removes only Flowio's saved copy." },
    ],
    tips: [
      "Pulling the same link again refreshes the report.",
      "A report still scanning in GHL appears as a short placeholder. Run Sync from GHL again later to fill it in.",
      "The local rank map needs a Google Maps key configured for your agency. Without it you still get the competitor list.",
    ],
    articles: ["insights-audit-reports-guide"],
  },
};
